'use client';

import React, { memo, useMemo } from 'react';
import { TextField, PhoneField, SelectField, TextAreaField, FormErrors } from './FormField';
import { STATE_OPTIONS, PRODUCT_OPTIONS } from '@/lib/data/formOptions';

interface FormFieldsProps {
  errors?: FormErrors;
  selectedProduct?: 'fridgeBottle' | 'makhanaJar' | null;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onValueChange: (name: string, value: string) => void;
}

/**
 * Form Fields Component
 * Renders the grid of form fields with proper layout
 * Memoized to prevent unnecessary re-renders when parent components update
 */
const FormFields = memo(function FormFields({ errors, selectedProduct, onBlur, onValueChange }: FormFieldsProps) {
  const productDefault = useMemo(() => {
    if (selectedProduct === 'fridgeBottle') return 'pet-bottles';
    if (selectedProduct === 'makhanaJar') return 'food-can';
    return undefined;
  }, [selectedProduct]);

  const requestDetailsDefault = useMemo(() => {
    if (selectedProduct === 'fridgeBottle') {
      return (
        "I'm interested in your Fridge Bottles product. " +
        "Please provide more information about pricing, minimum order quantity (MOQ), and delivery options. " +
        "I'd also like to know about customization options available."
      );
    }
    if (selectedProduct === 'makhanaJar') {
      return (
        "I'm interested in your Food Cans (Makhana PET Can Jars) product. " +
        "Please provide more information about pricing, minimum order quantity (MOQ), and delivery options. " +
        "I'd also like to know about customization options available."
      );
    }
    return undefined;
  }, [selectedProduct]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name field */}
                <TextField
          id="name"
          name="name"
          label="Full Name"
          type="text"
          errors={errors}
                    placeholder="Enter your full name"
          onBlur={onBlur}
          onValueChange={onValueChange}
          required
        />
        
        {/* Email field */}
                <TextField
          id="email"
          name="email"
          label="Email"
          type="email"
          errors={errors}
                    placeholder="your@email.com"
          onBlur={onBlur}
          onValueChange={onValueChange}
          required
        />
        
        {/* Phone field */}
                <PhoneField
          id="phone"
          name="phone"
          label="Phone or WhatsApp"
          type="tel"
          errors={errors}
                    placeholder="Enter 10-digit number"
          maxLength={10}
          onBlur={onBlur}
          onValueChange={onValueChange}
          required
        />
        
        {/* Quantity field */}
                <TextField
          id="quantity"
          name="quantity"
          label="Quantity Needed"
          type="number"
          errors={errors}
                    placeholder="e.g., 1000 pieces, 50 kg"
          onBlur={onBlur}
          onValueChange={onValueChange}
          required
        />
        
        {/* State field */}
        <SelectField
          id="state"
          name="state"
          label="State"
          errors={errors}
          options={STATE_OPTIONS}
          placeholder="Select State"
          onValueChange={onValueChange}
          required
        />
        
        {/* Product field */}
        <SelectField
          id="product"
          name="product"
          label="Product Interest"
          errors={errors}
          options={PRODUCT_OPTIONS}
          placeholder="Select Product"
          defaultValue={productDefault}
          onValueChange={onValueChange}
          required
        />
      </div>
      
      {/* Message field */}
      <TextAreaField
        key={selectedProduct || 'none'}
        id="request_details"
        required
        name="request_details"
        label="Request Details"
        errors={errors}
        placeholder="Please describe your requirements in detail"
        defaultValue={requestDetailsDefault}
        onBlur={onBlur}
        onValueChange={onValueChange}
        className="mt-6"
      />
    </>
  );
});

// Add display name for better debugging in React DevTools
FormFields.displayName = 'FormFields';

export default FormFields;